import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../hooks/useTier';
import { usePricing } from '../hooks/usePricing';

const TIERS = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Perfect for trying out QuickMeet',
    cta: 'Current Plan',
    highlighted: false,
    features: {
      // Core Features
      'Ad-free meetings': false,
      'Meeting duration': 'Unlimited',
      'Participants': 'Up to 4',
      'HD video quality': '720p',

      // Recording & Storage
      'Cloud recordings': false,
      'Local recordings': true,
      'Recording storage': '1 GB',
      'Recording retention': '7 days',

      // Meeting Features
      'Screen sharing': true,
      'Virtual backgrounds': 'Basic (3)',
      'Meeting templates': true,
      'Waiting room': true,
      'Access codes': true,
      'Chat': true,
      'Emoji reactions': true,
      'Hand raising': true,

      // Scheduling
      'Scheduled meetings': '3 meetings',
      'Meeting reminders': false,
      'Calendar integration': false,
      'Recurring meetings': false,

      // Collaboration
      'Meeting notes': 'View only',
      'Meeting polls': 'Basic (Yes/No)',
      'Breakout rooms': false,
      'Whiteboard': false,

      // Advanced Features
      'AI transcription': false,
      'AI meeting summaries': false,
      'Action item extraction': false,
      'Personal meeting room': false,
      'Custom branding': false,
      'Analytics': false,
      'Priority support': false,
      'API access': false,

      // Team Features
      'Team workspace': false,
      'Shared resources': false,
      'Admin controls': false,
      'Audit logs': false,
      'SSO/SAML': false,
      'Dedicated support': false,
    }
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$15',
    period: 'per user/month',
    description: 'Best for professionals',
    cta: 'Upgrade to Pro',
    highlighted: false,
    popular: false,
    features: {
      // Core Features
      'Ad-free meetings': true,
      'Meeting duration': 'Unlimited',
      'Participants': 'Up to 12',
      'HD video quality': '1080p',

      // Recording & Storage
      'Cloud recordings': true,
      'Local recordings': true,
      'Recording storage': '100 GB',
      'Recording retention': 'Unlimited',

      // Meeting Features
      'Screen sharing': true,
      'Virtual backgrounds': 'Unlimited + Custom',
      'Meeting templates': true,
      'Waiting room': true,
      'Access codes': true,
      'Chat': true,
      'Emoji reactions': true,
      'Hand raising': true,

      // Scheduling
      'Scheduled meetings': '50 meetings',
      'Meeting reminders': true,
      'Calendar integration': true,
      'Recurring meetings': true,

      // Collaboration
      'Meeting notes': 'Full editing',
      'Meeting polls': 'Advanced (10 options)',
      'Breakout rooms': '10 rooms',
      'Whiteboard': true,

      // Advanced Features
      'AI transcription': true,
      'AI meeting summaries': true,
      'Action item extraction': true,
      'Personal meeting room': true,
      'Custom branding': true,
      'Analytics': 'Personal',
      'Priority support': true,
      'API access': false,

      // Team Features
      'Team workspace': false,
      'Shared resources': false,
      'Admin controls': false,
      'Audit logs': false,
      'SSO/SAML': false,
      'Dedicated support': false,
    }
  },
  {
    id: 'team',
    name: 'Team',
    price: '$20',
    period: 'per user/month',
    description: 'For small businesses (5-25 people)',
    cta: 'Start Team Trial',
    highlighted: true,
    popular: true,
    minSeats: 5,
    features: {
      // Core Features
      'Ad-free meetings': true,
      'Meeting duration': 'Unlimited',
      'Participants': 'Unlimited',
      'HD video quality': '1080p',

      // Recording & Storage
      'Cloud recordings': true,
      'Local recordings': true,
      'Recording storage': '1 TB',
      'Recording retention': 'Unlimited',

      // Meeting Features
      'Screen sharing': true,
      'Virtual backgrounds': 'Unlimited + Custom',
      'Meeting templates': true,
      'Waiting room': true,
      'Access codes': true,
      'Chat': true,
      'Emoji reactions': true,
      'Hand raising': true,

      // Scheduling
      'Scheduled meetings': 'Unlimited',
      'Meeting reminders': true,
      'Calendar integration': true,
      'Recurring meetings': true,

      // Collaboration
      'Meeting notes': 'Full editing + Sharing',
      'Meeting polls': 'Advanced (20 options)',
      'Breakout rooms': '50 rooms',
      'Whiteboard': true,

      // Advanced Features
      'AI transcription': true,
      'AI meeting summaries': true,
      'Action item extraction': true,
      'Personal meeting room': true,
      'Custom branding': true,
      'Analytics': 'Organization-wide',
      'Priority support': true,
      'API access': true,

      // Team Features
      'Team workspace': true,
      'Shared resources': true,
      'Admin controls': true,
      'Audit logs': true,
      'SSO/SAML': true,
      'Dedicated support': true,
    }
  }
];

const FEATURE_CATEGORIES = [
  {
    name: 'Core Features',
    features: ['Ad-free meetings', 'Meeting duration', 'Participants', 'HD video quality']
  },
  {
    name: 'Recording & Storage',
    features: ['Cloud recordings', 'Local recordings', 'Recording storage', 'Recording retention']
  },
  {
    name: 'Meeting Features',
    features: ['Screen sharing', 'Virtual backgrounds', 'Meeting templates', 'Waiting room', 'Access codes', 'Chat', 'Emoji reactions', 'Hand raising']
  },
  {
    name: 'Scheduling',
    features: ['Scheduled meetings', 'Meeting reminders', 'Calendar integration', 'Recurring meetings']
  },
  {
    name: 'Collaboration Tools',
    features: ['Meeting notes', 'Meeting polls', 'Breakout rooms', 'Whiteboard']
  },
  {
    name: 'Advanced Features',
    features: ['AI transcription', 'AI meeting summaries', 'Action item extraction', 'Personal meeting room', 'Custom branding', 'Analytics', 'Priority support', 'API access']
  },
  {
    name: 'Team & Enterprise',
    features: ['Team workspace', 'Shared resources', 'Admin controls', 'Audit logs', 'SSO/SAML', 'Dedicated support']
  }
];

export default function PricingComparison({ onClose, onSelectPlan }) {
  const { userProfile } = useAuth();
  const { tier } = useTier();
  const { isPremium } = useTier();
  const { pricing } = usePricing();
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  // Merge static tier data with dynamic pricing
  const dynamicTiers = TIERS.map(tier => {
    if (pricing && pricing[tier.id]) {
      return {
        ...tier,
        price: pricing[tier.id].price,
        period: pricing[tier.id].period
      };
    }
    return tier;
  });

  const handleSelectPlan = (tier) => {
    if (tier.id === 'free') {
      onClose();
      return;
    }

    if (tier.id === 'team') {
      // Open contact sales (could be email or external form)
      window.location.href = 'mailto:sales@quickmeet.cam?subject=Team Plan Inquiry';
      return;
    }

    // For Pro tier, proceed to checkout
    if (onSelectPlan) {
      onSelectPlan(tier.id);
    }
  };

  const renderFeatureValue = (value) => {
    if (value === true) {
      return (
        <svg className="w-5 h-5 text-green-600 dark:text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    }
    if (value === false) {
      return (
        <svg className="w-5 h-5 text-red-500 dark:text-red-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      );
    }
    return <span className="text-sm text-[#1d1d1f] dark:text-white font-medium text-center">{value}</span>;
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in" onClick={onClose}>
      <div className="bg-white dark:bg-[#1c1c1e] rounded-2xl max-w-7xl w-full max-h-[95vh] overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 sm:p-8 border-b border-[#d2d2d7] dark:border-[#2c2c2e] bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1d1d1f] dark:text-white mb-2">Choose Your Plan</h2>
              <p className="text-base text-[#86868b] dark:text-[#8e8e93]">
                Select the perfect plan for your needs. Upgrade or downgrade anytime.
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-white/50 dark:hover:bg-[#2c2c2e] flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Pricing Cards + Feature Table */}
        <div className="overflow-y-auto max-h-[calc(95vh-180px)]">
          {/* Pricing Cards */}
          <div className="p-6 sm:p-8 grid grid-cols-1 md:grid-cols-3 gap-6 bg-[#f5f5f7] dark:bg-[#0d0d0d]">
            {dynamicTiers.map(tier => (
              <div
                key={tier.id}
                className={`relative rounded-2xl p-6 ${
                  tier.highlighted
                    ? 'bg-gradient-to-br from-[#06c] to-[#0055d4] text-white shadow-2xl scale-105 border-4 border-blue-400'
                    : 'bg-white dark:bg-[#1c1c1e] border-2 border-[#d2d2d7] dark:border-[#2c2c2e]'
                }`}
              >
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-yellow-400 text-black px-4 py-1 rounded-full text-xs font-bold uppercase">
                    Most Popular
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className={`text-2xl font-bold mb-2 ${tier.highlighted ? 'text-white' : 'text-[#1d1d1f] dark:text-white'}`}>
                    {tier.name}
                  </h3>
                  <div className="mb-3">
                    <span className={`text-5xl font-bold ${tier.highlighted ? 'text-white' : 'text-[#1d1d1f] dark:text-white'}`}>
                      {tier.price}
                    </span>
                    <span className={`text-sm ml-2 ${tier.highlighted ? 'text-white/80' : 'text-[#86868b] dark:text-[#8e8e93]'}`}>
                      {tier.period}
                    </span>
                  </div>
                  {tier.minSeats && (
                    <p className={`text-sm ${tier.highlighted ? 'text-white/80' : 'text-[#86868b] dark:text-[#8e8e93]'}`}>
                      Minimum {tier.minSeats} seats
                    </p>
                  )}
                  <p className={`text-sm mt-2 ${tier.highlighted ? 'text-white/90' : 'text-[#86868b] dark:text-[#8e8e93]'}`}>
                    {tier.description}
                  </p>
                </div>

                <button
                  onClick={() => handleSelectPlan(tier)}
                  disabled={isPremium && tier.id === 'pro'}
                  className={`w-full py-3 rounded-lg font-semibold transition-all ${
                    tier.highlighted
                      ? 'bg-white text-[#06c] hover:shadow-xl hover:scale-105'
                      : isPremium && tier.id === 'pro'
                        ? 'bg-[#d2d2d7] dark:bg-[#2c2c2e] text-[#86868b] cursor-not-allowed'
                        : 'bg-[#06c] text-white hover:bg-[#0077ed] hover:shadow-lg'
                  }`}
                >
                  {isPremium && tier.id === 'pro' ? 'Current Plan' : tier.cta}
                </button>
              </div>
            ))}
          </div>

          {/* Feature Comparison Table */}
          <div className="p-6 sm:p-8">
            <h3 className="text-2xl font-bold text-[#1d1d1f] dark:text-white mb-6 text-center">
              Compare All Features
            </h3>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-[#d2d2d7] dark:border-[#2c2c2e]">
                    <th className="p-4 text-left text-sm font-bold text-[#86868b] dark:text-[#8e8e93] uppercase tracking-wider">
                      Features
                    </th>
                    {dynamicTiers.map(tier => (
                      <th key={tier.id} className="p-4 text-center min-w-[150px]">
                        <div className={`text-lg font-bold ${tier.highlighted ? 'text-[#06c]' : 'text-[#1d1d1f] dark:text-white'}`}>
                          {tier.name}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {FEATURE_CATEGORIES.map((category, categoryIndex) => (
                    <>
                      <tr key={category.name} className={categoryIndex > 0 ? 'border-t-2 border-[#d2d2d7] dark:border-[#2c2c2e]' : ''}>
                        <td colSpan={4} className="p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e]">
                          <h4 className="text-sm font-bold text-[#1d1d1f] dark:text-white uppercase tracking-wider">
                            {category.name}
                          </h4>
                        </td>
                      </tr>
                      {category.features.map(feature => (
                        <tr key={feature} className="border-b border-[#d2d2d7] dark:border-[#2c2c2e] hover:bg-[#f5f5f7] dark:hover:bg-[#2c2c2e]/50 transition-colors">
                          <td className="p-4 text-sm text-[#1d1d1f] dark:text-white font-medium">
                            {feature}
                          </td>
                          {dynamicTiers.map(tier => (
                            <td key={tier.id} className="p-4 text-center">
                              {renderFeatureValue(tier.features[feature])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[#d2d2d7] dark:border-[#2c2c2e] bg-[#f5f5f7] dark:bg-[#0d0d0d] text-center">
          <p className="text-sm text-[#86868b] dark:text-[#8e8e93]">
            All plans include end-to-end encryption and P2P video calls. Cancel anytime, no commitments.
          </p>
        </div>
      </div>
    </div>
  );
}
